import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
} from 'react';
import * as styles from './Textarea.module.css';

export type TextareaVariant = 'primary' | 'secondary' | 'outline';
export type TextareaSize = 'sm' | 'md' | 'lg';

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  variant?: TextareaVariant;
  size?: TextareaSize;
  label?: React.ReactNode;
  error?: React.ReactNode;
  fullWidth?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  textareaClassName?: string;
  maxHeight?: number | string;
  minHeight?: number | string;
  autoResize?: boolean;
  manualResize?: boolean;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      label,
      error,
      fullWidth = false,
      className = '',
      wrapperClassName = '',
      labelClassName = '',
      textareaClassName = '',
      maxHeight,
      minHeight,
      autoResize = true,
      manualResize = false,
      onChange,
      disabled,
      style,
      value,
      defaultValue,
      id,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const generatedId = useId();
    const generatedErrorId = useId();
    const textareaId = id ?? generatedId;
    const hasError = Boolean(error);
    const s = styles as Record<string, string>;

    useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

    const adjustHeight = useCallback(() => {
      const textarea = textareaRef.current;

      if (!textarea || !autoResize || manualResize) return;

      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }, [autoResize, manualResize]);

    useEffect(() => {
      adjustHeight();
    }, [value, defaultValue, adjustHeight]);

    const handleChange: React.ChangeEventHandler<
      HTMLTextAreaElement
    > = event => {
      adjustHeight();
      onChange?.(event);
    };

    const wrapperClasses = [
      s.wrapper || '',
      fullWidth ? s.fullWidth || '' : '',
      disabled ? s.disabled || '' : '',
      className,
      wrapperClassName,
    ]
      .filter(Boolean)
      .join(' ');

    const textareaClasses = [
      s.textarea || '',
      s[variant] || '',
      s[size] || '',
      hasError ? s.hasError || '' : '',
      manualResize ? s.manualResize || '' : '',
      textareaClassName,
    ]
      .filter(Boolean)
      .join(' ');

    const textareaStyle: React.CSSProperties = {
      ...style,
      ...(maxHeight !== undefined ? { maxHeight } : {}),
      ...(minHeight !== undefined ? { minHeight } : {}),
    };

    const describedBy = [ariaDescribedBy, hasError ? generatedErrorId : '']
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses}>
        {label && (
          <label
            className={`${s.label || ''} ${labelClassName}`.trim()}
            htmlFor={textareaId}
          >
            {label}
          </label>
        )}
        <textarea
          ref={textareaRef}
          id={textareaId}
          value={value}
          defaultValue={defaultValue}
          disabled={disabled}
          aria-describedby={describedBy || undefined}
          aria-invalid={ariaInvalid ?? (hasError ? true : undefined)}
          className={textareaClasses}
          style={textareaStyle}
          onChange={handleChange}
          {...props}
        />
        {hasError && (
          <span className={s.error || ''} id={generatedErrorId}>
            {error}
          </span>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
